import { createContext, useContext, useEffect, useState } from 'react'
import { io } from 'socket.io-client'

const SocketContext = createContext()

export const useSocket = () => {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider')
  }
  return context
}

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null)
  const [connected, setConnected] = useState(false)

  useEffect(() => {
    // Get API URL from env or default
    let apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001'
    
    // If page is loaded over HTTPS and API URL is HTTP, convert to HTTPS
    if (window.location.protocol === 'https:' && apiUrl.startsWith('http://')) {
      // Convert HTTP to HTTPS for secure WebSocket (WSS)
      apiUrl = apiUrl.replace('http://', 'https://')
      console.warn('⚠️ Page loaded over HTTPS, converting Socket.io URL to HTTPS:', apiUrl)
    }
    
    // Initialize socket connection
    const newSocket = io(apiUrl, {
      transports: ['websocket', 'polling'],
      // Use secure WebSocket when page is HTTPS
      secure: window.location.protocol === 'https:',
      // Auto-upgrade to secure connection
      upgrade: true
    })

    newSocket.on('connect', () => {
      console.log('✅ Socket connected:', newSocket.id)
      setConnected(true)
    })

    newSocket.on('disconnect', () => {
      console.log('❌ Socket disconnected')
      setConnected(false)
    })

    newSocket.on('error', (error) => {
      console.error('❌ Socket error:', error)
    })

    newSocket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error)
    })

    setSocket(newSocket)

    return () => {
      newSocket.close()
    }
  }, [])

  // Join chat room
  const joinChat = (chatId) => {
    if (socket) {
      socket.emit('join-chat', chatId)
    }
  }

  // Leave chat room
  const leaveChat = (chatId) => {
    if (socket) {
      socket.emit('leave-chat', chatId)
    }
  }

  // Join overlay room
  const joinOverlay = (overlayId) => {
    if (socket) {
      socket.emit('join-overlay', overlayId)
    }
  }

  // Categorize comment
  const categorizeComment = (chatId, commentId, category) => {
    if (socket) {
      console.log('Socket context: Emitting categorize-comment', { chatId, commentId, category })
      socket.emit('categorize-comment', { chatId, commentId, category })
    } else {
      console.error('Socket context: Socket not available for categorize-comment')
    }
  }

  // Toggle comment visibility
  const toggleCommentVisibility = (chatId, commentId, isVisible) => {
    if (socket) {
      socket.emit('toggle-comment-visibility', { chatId, commentId, isVisible })
    }
  }

  // Dismiss comment
  const dismissComment = (chatId, commentId) => {
    if (socket) {
      socket.emit('dismiss-comment', { chatId, commentId })
    }
  }

  // Listen for new comments
  const onNewComments = (callback) => {
    if (socket) {
      socket.on('new-comments', callback)
    }
  }

  // Listen for comment categorization updates
  const onCommentCategorized = (callback) => {
    if (socket) {
      socket.on('comment-categorized', callback)
    }
  }

  // Listen for comment visibility updates
  const onCommentVisibilityUpdated = (callback) => {
    if (socket) {
      socket.on('comment-visibility-updated', callback)
    }
  }

  // Listen for comment dismissal
  const onCommentDismissed = (callback) => {
    if (socket) {
      socket.on('comment-dismissed', callback)
    }
  }

  // Listen for chat updates
  const onChatUpdated = (callback) => {
    if (socket) {
      socket.on('chat-updated', callback)
    }
  }

  // Listen for overlay updates
  const onOverlayUpdated = (callback) => {
    if (socket) {
      socket.on('overlay-updated', callback)
    }
  }

  // Emit overlay update
  const emitOverlayUpdate = (overlayId, updateData) => {
    if (socket) {
      socket.emit('overlay-update', { overlayId, data: updateData })
    }
  }

  // Remove all listeners for a specific event
  const removeListener = (event, callback) => {
    if (socket) {
      socket.off(event, callback)
    }
  }

  const value = {
    socket,
    connected,
    joinChat,
    leaveChat,
    joinOverlay,
    categorizeComment,
    toggleCommentVisibility,
    dismissComment,
    onNewComments,
    onCommentCategorized,
    onCommentVisibilityUpdated,
    onCommentDismissed,
    onChatUpdated,
    onOverlayUpdated,
    emitOverlayUpdate,
    removeListener
  }

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  )
}